import { useState, useEffect } from "react";
import axios from "axios";
import TrendCard from "@/components/TrendCard";
import { Compass, TrendingUp, Flame, Clock, Globe } from "lucide-react";
import { motion } from "framer-motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/App";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExplorePage = () => {
  const { user } = useAuth();
  const [hotTrends, setHotTrends] = useState([]);
  const [allTrends, setAllTrends] = useState([]);
  const [platforms, setPlatforms] = useState({ social: [], marketplaces: [] });
  const [loading, setLoading] = useState(true);
  const [savedTrends, setSavedTrends] = useState(new Set());

  useEffect(() => {
    fetchData();
    if (user) {
      fetchSavedTrends();
    }
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [hotRes, allRes, platformsRes] = await Promise.all([
        axios.get(`${API}/trends/hot?limit=10`),
        axios.get(`${API}/trends?limit=20`),
        axios.get(`${API}/platforms`)
      ]);
      setHotTrends(hotRes.data);
      setAllTrends(allRes.data);
      setPlatforms(platformsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedTrends = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/user/saved-trends`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const saved = new Set(response.data.saved_trends.map(s => s.trend.trend_id));
      setSavedTrends(saved);
    } catch (error) {
      console.error("Error fetching saved trends:", error);
    }
  };

  const handleSave = async (trendId) => {
    if (!user) {
      toast.error("Please sign in to save trends");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/trends/${trendId}/save`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.saved) {
        setSavedTrends(prev => new Set([...prev, trendId]));
        toast.success("Trend saved!");
      } else {
        setSavedTrends(prev => {
          const next = new Set(prev);
          next.delete(trendId);
          return next;
        });
      }
    } catch (error) {
      toast.error("Failed to save trend");
    }
  };

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-2 mb-2">
          <Compass className="w-6 h-6 text-[#7C3AED]" />
          <span className="text-sm font-mono uppercase tracking-widest text-gray-500">
            EXPLORE
          </span>
        </div>
        <h1 className="text-4xl md:text-6xl font-extrabold tracking-tighter mb-4">
          Explore Trends
        </h1>
        <p className="text-gray-600 max-w-xl">
          Discover trending topics across social media platforms and marketplaces. 
          All data links to original sources.
        </p>
      </motion.div>

      {/* Platform Coverage */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-8"
      >
        <h2 className="text-sm font-mono uppercase tracking-widest text-gray-500 mb-4 flex items-center gap-2">
          <Globe className="w-4 h-4" />
          PLATFORMS WE TRACK
        </h2>
        <div className="flex flex-wrap gap-2">
          {platforms.social?.map((platform) => (
            <div
              key={platform.id}
              className="flex items-center gap-2 px-3 py-2 border-2 border-black"
              style={{ borderLeftColor: platform.color, borderLeftWidth: 4 }}
            >
              <span className="font-bold text-sm">{platform.name}</span>
            </div>
          ))}
          {platforms.marketplaces?.map((platform) => (
            <div
              key={platform.id}
              className="flex items-center gap-2 px-3 py-2 border-2 border-black"
              style={{ borderLeftColor: platform.color, borderLeftWidth: 4 }}
            >
              <span className="font-bold text-sm">{platform.name}</span>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Content Tabs */}
      <Tabs defaultValue="hot" className="w-full">
        <TabsList className="border-2 border-black bg-white p-0 h-auto mb-6">
          <TabsTrigger
            value="hot"
            data-testid="tab-hot"
            className="flex items-center gap-2 px-6 py-3 data-[state=active]:bg-black data-[state=active]:text-white rounded-none font-bold"
          >
            <Flame className="w-4 h-4" />
            Hottest
          </TabsTrigger>
          <TabsTrigger
            value="rising"
            data-testid="tab-rising"
            className="flex items-center gap-2 px-6 py-3 data-[state=active]:bg-black data-[state=active]:text-white rounded-none font-bold"
          >
            <TrendingUp className="w-4 h-4" />
            Rising
          </TabsTrigger>
          <TabsTrigger
            value="all"
            data-testid="tab-all"
            className="flex items-center gap-2 px-6 py-3 data-[state=active]:bg-black data-[state=active]:text-white rounded-none font-bold"
          >
            <Clock className="w-4 h-4" />
            All Trends
          </TabsTrigger>
        </TabsList>

        <TabsContent value="hot">
          <div className="flex items-center gap-2 mb-4">
            <Flame className="w-5 h-5 text-[#FF0099]" />
            <span className="font-bold uppercase tracking-wide text-sm">
              Fastest Growing Trends
            </span>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="h-64 border-2 border-gray-200 bg-gray-100 skeleton"
                />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="hot-trends">
              {hotTrends.map((trend, index) => (
                <TrendCard 
                  key={trend.trend_id} 
                  trend={trend} 
                  index={index}
                  onSave={handleSave}
                  isSaved={savedTrends.has(trend.trend_id)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="rising">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-green-500" />
            <span className="font-bold uppercase tracking-wide text-sm">
              Rising Trends
            </span>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="h-64 border-2 border-gray-200 bg-gray-100 skeleton"
                />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rising-trends">
              {allTrends.filter(t => t.velocity === "rising").map((trend, index) => (
                <TrendCard 
                  key={trend.trend_id} 
                  trend={trend} 
                  index={index}
                  onSave={handleSave}
                  isSaved={savedTrends.has(trend.trend_id)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="all">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-[#7C3AED]" />
            <span className="font-bold uppercase tracking-wide text-sm">
              All Trends
            </span>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="h-64 border-2 border-gray-200 bg-gray-100 skeleton"
                />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="all-trends">
              {allTrends.map((trend, index) => (
                <TrendCard 
                  key={trend.trend_id} 
                  trend={trend} 
                  index={index}
                  onSave={handleSave}
                  isSaved={savedTrends.has(trend.trend_id)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ExplorePage;
